import { create } from 'zustand';
import admissionService from '../api/admissionService';
import logger from '../utils/logger';

const useAdmissionStore = create((set, get) => ({
  applications: [],
  loading: false,
  error: null,
  stats: null,

  fetchApplications: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await admissionService.getAllAdmissions(params);
      logger.log('Fetch Applications Response:', response);
      // API returns: { status, results, data: { applications, pagination } }
      // Filter out any invalid items (null, undefined, or non-objects)
      const validApplications = (response.data?.applications || []).filter(app => app && typeof app === 'object');
      set({ applications: validApplications, loading: false });
    } catch (error) {
      logger.error('Fetch Applications Error:', error);
      set({ error: error.response?.data?.message || 'Failed to fetch applications', loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await admissionService.getAdmissionStats();
      set({ stats: response.data });
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
    }
  },

  addApplication: async (applicationData) => {
    try {
      logger.log('Store: Calling API with data:', applicationData);
      const response = await admissionService.submitAdmission(applicationData);
      logger.log('Store: API Response:', response);
      
      // Only add the application if it's a valid object
      if (response.data?.application && typeof response.data.application === 'object') {
        set((state) => ({ applications: [...state.applications, response.data.application] }));
        return { success: true, data: response.data.application };
      } else {
        logger.error('Invalid application data received:', response.data);
        return { success: false, message: 'Invalid application data received from server' };
      }
    } catch (error) {
      logger.error('Store: API Error:', error);
      logger.error('Store: Error Response:', error.response?.data);
      
      // Log validation errors if they exist (could be array or object)
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errors.forEach((err, index) => {
            logger.error(`Validation Error ${index + 1}:`, {
              field: err.path || err.param || 'unknown',
              value: err.value,
              message: err.msg || err.message
            });
          });
        } else {
          logger.error('Validation Errors:', errors);
        }
      }
      
      // Extract error message
      const errorMessage = error.response?.data?.message || 
                          (Array.isArray(error.response?.data?.errors) ? error.response?.data?.errors[0]?.message : null) ||
                          'Failed to submit application';
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  },

  updateApplicationStatus: async (applicationId, newStatus, notes = '') => {
    try {
      const response = await admissionService.updateAdmissionStatus(applicationId, newStatus, notes);
      // Update the application in the state with the response from the API
      set((state) => ({ 
        applications: state.applications.map(app => 
          app._id === applicationId ? response.data.application : app
        ) 
      }));
      return { success: true, data: response.data.application };
    } catch (error) {
      logger.error('Failed to update status:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update status' };
    }
  },

  deleteApplication: async (applicationId) => {
    try {
      await admissionService.deleteAdmission(applicationId);
      set((state) => ({ applications: state.applications.filter(app => app._id !== applicationId) }));
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete application' };
    }
  },

  getApplicationsByStatus: (status) => {
    const { applications } = get();
    // Filter out invalid items
    const validApplications = applications.filter(app => app && typeof app === 'object');
    if (status === 'all') return validApplications;
    return validApplications.filter(app => app.status === status);
  },

  getApplicationsByGrade: (grade) => {
    const { applications } = get();
    // Filter out invalid items
    const validApplications = applications.filter(app => app && typeof app === 'object');
    if (grade === 'all') return validApplications;
    return validApplications.filter(app => app.gradeAppliedFor === grade);
  },

  searchApplications: (searchTerm) => {
    const { applications } = get();
    // Filter out invalid items
    const validApplications = applications.filter(app => app && typeof app === 'object');
    if (!searchTerm) return validApplications;
    const term = searchTerm.toLowerCase();
    return validApplications.filter(app => app.studentName?.toLowerCase().includes(term) || app.fatherName?.toLowerCase().includes(term) || app.applicationNumber?.toLowerCase().includes(term));
  },

  getStats: () => {
    const { applications } = get();
    // Filter out any undefined, null, or invalid items
    const validApplications = applications.filter(app => app && typeof app === 'object');
    const total = validApplications.length;
    const pending = validApplications.filter(app => app.status === 'Pending').length;
    const approved = validApplications.filter(app => app.status === 'Accepted').length;
    const rejected = validApplications.filter(app => app.status === 'Rejected').length;
    return { total, pending, approved, rejected };
  },

  // Alias for compatibility
  getStatistics: () => {
    return get().getStats();
  },

  // Combined filter function for Admissions component
  getFilteredApplications: (searchTerm = '', statusFilter = 'all', gradeFilter = 'all') => {
    let { applications } = get();
    
    // Filter out any undefined, null, or invalid items first
    applications = applications.filter(app => app && typeof app === 'object');
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      applications = applications.filter(app => 
        app.studentName?.toLowerCase().includes(term) || 
        app.fatherName?.toLowerCase().includes(term) || 
        app.applicationNumber?.toLowerCase().includes(term)
      );
    }
    
    // Filter by status
    if (statusFilter && statusFilter !== 'all') {
      applications = applications.filter(app => app.status === statusFilter);
    }
    
    // Filter by grade
    if (gradeFilter && gradeFilter !== 'all') {
      applications = applications.filter(app => app.gradeAppliedFor === gradeFilter);
    }
    
    return applications;
  },

  // Bulk update status
  bulkUpdateStatus: async (applicationIds, newStatus, notes = '') => {
    try {
      const updatePromises = applicationIds.map(id => 
        admissionService.updateAdmissionStatus(id, newStatus, notes)
      );
      const results = await Promise.all(updatePromises);
      
      // Update local state
      set((state) => ({
        applications: state.applications.map(app => {
          if (applicationIds.includes(app._id)) {
            return { ...app, status: newStatus };
          }
          return app;
        })
      }));
      
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to bulk update' };
    }
  },

  // Initialize sample data (for development/demo purposes)
  initializeSampleData: () => {
    // This is a no-op function for production
    // In development, you would fetch real data
    const { applications } = get();
    if (applications.length === 0) {
      // Fetch applications from the API
      get().fetchApplications();
    }
  }
}));

export default useAdmissionStore;
